import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Download, Loader2, Pause, Play, Plus, Trash2, Zap } from 'lucide-react';
import {
  automationApi,
  type AutomationEntityType,
  type AutomationExecutionOutcome,
  type AutomationExecutionRun,
  type AutomationPending,
  type AutomationPendingRun,
  type AutomationRule,
} from '@/api/automationApi';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/ui/Toast';
import { Spinner } from '@/components/ui/Spinner';
import { Button } from '@/components/ui/Button';
import { Table, TableWrap, TableHeader, TableRow, TableHead, TableCell } from '@/components/ui/Table';
import { BidSnapshotsSection } from '@/components/bidSnapshots/BidSnapshotsSection';
import { BidPlacementSection } from '@/components/hourlyBudget/BidPlacementSection';
import { DaypartingSection } from '@/components/hourlyBudget/DaypartingSection';
import { DAYPARTING_TAB } from '@/components/hourlyBudget/daypartingTab';
import { SmartBudgetSection } from '@/components/smartBudget/SmartBudgetSection';
import { AutomationCampaignPicker } from '@/components/automation/AutomationCampaignPicker';
import { AutomationMetricSelect } from '@/components/automation/AutomationMetricSelect';
import { PendingRunsTable } from '@/components/automation/PendingRunsTable';
import { TermListsSection } from '@/components/automation/TermListsSection';
import {
  AUTOMATION_RULE_CHIPS,
  defaultAdTypes,
  defaultConditionGroups,
  defaultHarvestOptions,
  defaultTargetMatchTypes,
  entityNoun,
  entityTypeLabel,
  actionUsesNumericRestore,
  catalogActionIsRevertible,
  formatConditionGroupLine,
  isAutomationRuleEntity,
  preserveRevertAtDayEnd,
  MAX_AND_CONDITIONS,
  mergeAutomationRuleOptions,
  normalizePlacementGroups,
  placementActionHasAdjustments,
  scopeCopy,
  serializePlacementAdjustments,
  parsePlacementAdjustmentRows,
  TARGET_MATCH_TYPES,
  type HarvestOptions,
  type TargetMatchTypeId,
} from '@/components/automation/automationTypes';
import {
  PlacementConditionCell,
  PlacementMetricsCell,
} from '@/components/automation/ExecutionPlacementDetails';
import { PlacementValueActionFields } from '@/components/automation/PlacementValueActionFields';
import { TargetActionFields } from '@/components/automation/TargetActionFields';
import {
  entitiesPayload,
  entitiesPayloadForScope,
  inferScopeKind,
  listEntityTypeForScope,
  mapEntityApiItems,
  mergeSelectedNames,
  pruneSelectedByAdTypes,
  ruleScopeHint,
  scopeHintNoun,
  selectedFromRuleEntities,
  usesCampaignAdGroupScope,
  usesPortfolioCampaignScope,
  type AutomationCampaignOption,
  type CampaignAdGroupScopeKind,
} from '@/components/automation/automationCampaignScope';
import {
  AUTOMATIONS_TAB_BID_SNAPSHOTS,
  AUTOMATIONS_TAB_RULES,
  AUTOMATIONS_TAB_SMART_BUDGET,
  buildAutomationsSearchParams,
  isRulesTabActive,
  parseAutomationsNav,
} from '@/components/automation/automationsNav';
import { fetchAllPendingForRun } from '@/utils/automationPendingRuns';
import { cn } from '@/utils/cn';
import {
  downloadAutomationExecutionsCsv,
  fetchAllAutomationExecutions,
} from '@/utils/automationExecutionCsv';
import {
  formatActionType,
  formatConditionColumnText,
  formatEvaluationSummary,
  type ConditionMetricMeta,
  executionValueLines,
  formatExecutionStatus,
  formatExecutionValue,
  formatRevertStatus,
  groupBadgeStatusFromCounts,
  pendingEvaluation,
  pendingRowToChange,
  summarizeGroupChange,
  summarizeStatusCounts,
} from '@/utils/automationExecutionChange';

function pendingActionType(row: AutomationPending): string {
  return String((row.plannedAction as { action_type?: string } | null)?.action_type ?? '—');
}

type AutomationMetric = {
  key: string;
  label: string;
  unit?: string;
  operators?: string[];
  value_types?: string[];
};

type ConditionLeaf = {
  metric: string;
  operator: string;
  value_type: string;
  value: number | string;
};

type ConditionGroup = {
  kind: string;
  conditions: { op: string; children: ConditionLeaf[] } | null;
  action: {
    type: string;
    value?: number;
    message?: string;
    adjustments?: Array<{ placement: string; mode: string; value: number }>;
    revertAtDayEnd?: boolean;
    revertValue?: number;
  };
};

const OPERATOR_OPTIONS: Array<{ value: string; label: string; symbol: string }> = [
  { value: 'gt', label: 'Greater than', symbol: '>' },
  { value: 'gte', label: 'Greater than or equal', symbol: '≥' },
  { value: 'lt', label: 'Less than', symbol: '<' },
  { value: 'lte', label: 'Less than or equal', symbol: '≤' },
  { value: 'eq', label: 'Equal to', symbol: '=' },
  { value: 'neq', label: 'Not equal to', symbol: '≠' },
];

const CONDITION_ROW_GRID =
  'grid grid-cols-[minmax(0,1.1fr)_minmax(9.5rem,1.4fr)_minmax(0,0.8fr)_1.75rem] gap-2';

function defaultMetricValue(metric: AutomationMetric | undefined): number | string {
  if (!metric) return 0;
  if (metric.key === 'match_type') return 'EXACT';
  if (metric.unit === 'date') return todayIso();
  if (metric.unit === 'string' || metric.key === 'status' || metric.key === 'parent_campaign_status') {
    return 'ENABLED';
  }
  return 0;
}

function emptyConditionLeaf(metrics: AutomationMetric[]): ConditionLeaf {
  const metric = metrics[0];
  if (metric && (metric.unit === 'string' || metric.unit === 'date' || metric.key === 'status')) {
    return {
      metric: metric.key,
      operator: metric.unit === 'date' ? 'gt' : 'eq',
      value_type: 'fixed',
      value: defaultMetricValue(metric),
    };
  }
  return { metric: metric?.key ?? 'acos', operator: 'gt', value_type: 'fixed', value: 0 };
}

function isEnumMetric(metric: AutomationMetric | undefined, key: string): boolean {
  return (
    metric?.unit === 'string' ||
    key === 'status' ||
    key === 'parent_campaign_status' ||
    key === 'match_type'
  );
}

function isDateMetric(metric: AutomationMetric | undefined, key: string): boolean {
  return metric?.unit === 'date' || key === 'parent_campaign_start_date';
}

function coerceFixedValueTypes(groups: ConditionGroup[]): ConditionGroup[] {
  return groups.map((g) => {
    const children = g.conditions?.children;
    if (!children?.length) return g;
    return {
      ...g,
      conditions: {
        op: g.conditions?.op ?? 'and',
        children: children.map((c) =>
          c.value_type === 'relative_pct' || c.value_type === 'relative_abs'
            ? { ...c, value_type: 'fixed' }
            : c
        ),
      },
    };
  });
}

function initialRuleGroups(
  entityType: string,
  initial?: AutomationRule | null
): ConditionGroup[] {
  const raw = (initial?.conditionGroups as ConditionGroup[] | undefined)?.length
    ? (initial!.conditionGroups as ConditionGroup[])
    : (defaultConditionGroups(entityType) as ConditionGroup[]);
  const groups = entityType === 'placement' ? (normalizePlacementGroups(raw) as ConditionGroup[]) : raw;
  return coerceFixedValueTypes(groups);
}

function operatorsForMetric(metric: AutomationMetric | undefined) {
  const allowed = metric?.operators;
  if (!allowed?.length) return OPERATOR_OPTIONS;
  const filtered = OPERATOR_OPTIONS.filter((o) => allowed.includes(o.value));
  return filtered.length ? filtered : OPERATOR_OPTIONS;
}

function updateGroupChildren(
  groups: ConditionGroup[],
  gi: number,
  updater: (children: ConditionLeaf[]) => ConditionLeaf[]
): ConditionGroup[] {
  return groups.map((g, i) => {
    if (i !== gi) return g;
    const children = updater([...(g.conditions?.children ?? [])]);
    return {
      ...g,
      conditions: { op: g.conditions?.op ?? 'and', children },
    };
  });
}

function ruleLabel(ruleName: string | null | undefined, ruleId: string): string {
  return ruleName || ruleId.slice(0, 8);
}

function ExecutionCsvExportButton({
  onExport,
  disabled,
  label = 'Export CSV',
}: {
  onExport: () => void | Promise<void>;
  disabled?: boolean;
  label?: string;
}) {
  const [pending, setPending] = useState(false);

  return (
    <button
      type="button"
      disabled={disabled || pending}
      onClick={() => {
        const result = onExport();
        if (result && typeof (result as Promise<void>).then === 'function') {
          setPending(true);
          void (result as Promise<void>)
            .catch(() => undefined)
            .finally(() => setPending(false));
        }
      }}
      className="text-sm px-3 py-1.5 border border-border rounded-lg hover:bg-surface-muted font-medium inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {pending ? (
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
      ) : (
        <Download className="w-3.5 h-3.5" />
      )}
      {label}
    </button>
  );
}

function RevertAtDayEndSwitch({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-2 text-sm">
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label="Revert at day end"
        onClick={() => onChange(!checked)}
        className={cn(
          'relative inline-flex h-5 w-9 shrink-0 rounded-full transition-colors',
          checked ? 'bg-brand' : 'bg-border'
        )}
      >
        <span
          className={cn(
            'absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-surface shadow transition-transform',
            checked && 'translate-x-4'
          )}
        />
      </button>
      Revert at day end
    </label>
  );
}

function statusBadgeClass(status: string): string {
  if (
    status === 'active' ||
    status === 'executed' ||
    status === 'confirmed' ||
    status === 'success' ||
    status === 'succeeded' ||
    status === 'skipped_noop'
  ) {
    return 'border-success/20 bg-success-subtle text-success-ink';
  }
  if (status === 'failed' || status === 'cancelled' || status === 'expired') {
    return 'border-error/20 bg-error-subtle text-error-ink';
  }
  if (status === 'pending') {
    return 'border-warning/20 bg-warning-subtle text-warning-ink';
  }
  return 'border-border bg-surface-muted text-foreground-muted';
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function formatRuleDate(iso: string): string {
  const [year, month, day] = iso.slice(0, 10).split('-').map(Number);
  if (!year || !month || !day) return iso;
  return new Date(year, month - 1, day).toLocaleDateString();
}

function ruleBadgeStatus(rule: AutomationRule): string {
  return rule.effectiveStatus ?? rule.status;
}

function formatHourLabel(hour: number): string {
  const h = ((hour % 24) + 24) % 24;
  if (h === 0) return '12 AM';
  if (h === 12) return '12 PM';
  if (h < 12) return `${h} AM`;
  return `${h - 12} PM`;
}

function initialExecutionHours(rule?: AutomationRule | null): number[] {
  if (rule?.executionHours?.length) {
    return [...new Set(rule.executionHours)].sort((a, b) => a - b);
  }
  if (rule?.executionHour != null) return [rule.executionHour];
  return [9];
}

function formatFrequencyCell(rule: AutomationRule): string {
  if (rule.frequency === 'hourly') return 'hourly';
  const hours = rule.executionHours?.length
    ? rule.executionHours
    : rule.executionHour != null
      ? [rule.executionHour]
      : [];
  if (!hours.length) return rule.frequency;
  return `${rule.frequency} · ${hours.join(', ')}`;
}

function RuleBuilderModal({
  profileId,
  entityType,
  initial,
  onClose,
}: {
  profileId: string;
  entityType: AutomationEntityType;
  initial?: AutomationRule | null;
  onClose: () => void;
}) {
  const { showToast } = useToast();
  const qc = useQueryClient();
  const allowedAdTypes = defaultAdTypes(entityType);
  const copy = scopeCopy(entityType);
  const splitScope = usesCampaignAdGroupScope(entityType);
  const portfolioScope = usesPortfolioCampaignScope(entityType);
  const [name, setName] = useState(initial?.name ?? '');
  const [startDate, setStartDate] = useState(initial?.startDate ?? todayIso());
  const [noEndDate, setNoEndDate] = useState(!initial?.endDate);
  const [endDate, setEndDate] = useState(initial?.endDate ?? '');
  const [adTypes, setAdTypes] = useState<string[]>(
    initial?.adTypes?.length
      ? initial.adTypes.filter((t) => allowedAdTypes.includes(t))
      : allowedAdTypes
  );
  const [lookbackDays, setLookbackDays] = useState(initial?.lookbackDays ?? 14);
  const [excludeDays, setExcludeDays] = useState(initial?.excludeDays ?? 1);
  const [executionMode, setExecutionMode] = useState(initial?.executionMode ?? 'automatic');
  const [frequency, setFrequency] = useState(initial?.frequency ?? 'daily');
  const [executionHours, setExecutionHours] = useState<number[]>(() => initialExecutionHours(initial));
  const [weeklyDays, setWeeklyDays] = useState<number[]>(initial?.weeklyDays ?? [1, 2, 3, 4, 5]);
  const [timezone, setTimezone] = useState(initial?.timezone ?? 'Asia/Kolkata');
  const [groups, setGroups] = useState<ConditionGroup[]>(() => initialRuleGroups(entityType, initial));
  const [harvest, setHarvest] = useState<HarvestOptions>(() =>
    defaultHarvestOptions(initial?.ruleOptions as Record<string, unknown> | undefined)
  );
  const [matchTypes, setMatchTypes] = useState<TargetMatchTypeId[]>(() =>
    defaultTargetMatchTypes(initial?.ruleOptions as Record<string, unknown> | undefined)
  );
  const [entityScope, setEntityScope] = useState<'all' | 'specific'>(
    initial?.entityScope === 'specific' ? 'specific' : 'all'
  );
  const [scopeKind, setScopeKind] = useState<CampaignAdGroupScopeKind>(() =>
    inferScopeKind(
      entityType,
      initial?.entityScope,
      initial?.entities,
      initial?.ruleOptions as Record<string, unknown> | undefined
    )
  );
  const [selectedEntities, setSelectedEntities] = useState<AutomationCampaignOption[]>(() =>
    selectedFromRuleEntities(initial?.entities)
  );
  const effectiveScope: 'all' | 'specific' = portfolioScope
    ? scopeKind === 'all'
      ? 'all'
      : 'specific'
    : entityScope;
  const listEntityType = listEntityTypeForScope(entityType, portfolioScope ? scopeKind : 'all');
  const pickerNoun =
    scopeKind === 'portfolios'
      ? 'portfolios'
      : splitScope
        ? scopeKind === 'ad_groups'
          ? 'ad groups'
          : 'campaigns'
        : entityType === 'placement'
          ? 'campaigns'
          : entityNoun(entityType);
  const pickerSearch =
    scopeKind === 'portfolios'
      ? copy.searchPortfolios || 'Search portfolios…'
      : splitScope && scopeKind === 'ad_groups'
        ? copy.searchAdGroups || copy.search
        : copy.search;

  const metricsQ = useQuery({
    queryKey: ['automation-meta', profileId, 'metrics', entityType],
    queryFn: () => automationApi.listMetrics(profileId, entityType).then((r) => r.data ?? []),
  });
  const actionsQ = useQuery({
    queryKey: ['automation-meta', profileId, 'actions', entityType],
    queryFn: () => automationApi.listActions(profileId, entityType).then((r) => r.data ?? []),
  });
  const entitiesQ = useQuery({
    queryKey: ['automation-entities', profileId, listEntityType, adTypes],
    queryFn: () =>
      automationApi
        .listEntities(profileId, {
          entity_type: listEntityType,
          ad_types: adTypes.join(','),
          state: 'ENABLED',
          limit: '200',
        })
        .then((r) => mapEntityApiItems(r.data?.items ?? [])),
    enabled: effectiveScope === 'specific' && adTypes.length > 0,
  });

  useEffect(() => {
    setSelectedEntities((prev) => pruneSelectedByAdTypes(prev, adTypes, scopeKind));
  }, [adTypes, scopeKind]);

  useEffect(() => {
    const items = entitiesQ.data;
    if (!items?.length) return;
    setSelectedEntities((prev) => mergeSelectedNames(prev, items));
  }, [entitiesQ.data]);

  const saveMut = useMutation({
    mutationFn: async (opts?: { enable?: boolean }) => {
      const enable = Boolean(opts?.enable);
      const nextStatus = initial?.id
        ? enable
          ? 'active'
          : (initial.status ?? 'paused')
        : enable
          ? 'active'
          : 'paused';
      const body = {
        name,
        entityType,
        startDate,
        endDate: noEndDate ? null : endDate || null,
        adTypes,
        lookbackDays,
        excludeDays,
        executionMode,
        frequency,
        executionHour: frequency === 'hourly' ? null : executionHours[0] ?? 9,
        executionHours: frequency === 'hourly' ? null : executionHours,
        weeklyDays: frequency === 'weekly' ? weeklyDays : null,
        timezone,
        entityScope: effectiveScope,
        entities:
          effectiveScope === 'specific'
            ? portfolioScope
              ? entitiesPayloadForScope(selectedEntities, entityType, scopeKind)
              : entitiesPayload(selectedEntities)
            : undefined,
        conditionGroups:
          entityType === 'placement'
            ? groups.map((g) => {
                const catalog = (actionsQ.data ?? []).filter((item) => item.enabled);
                return {
                  ...g,
                  action: preserveRevertAtDayEnd(
                    {
                      type: 'placement_value',
                      adjustments: serializePlacementAdjustments(parsePlacementAdjustmentRows(g.action), {
                        disableTopOfSearch: adTypes.length === 1 && adTypes[0] === 'SB',
                      }),
                    },
                    g.action.revertAtDayEnd,
                    catalog.length === 0 || catalogActionIsRevertible('placement_value', catalog),
                    g.action.revertValue
                  ),
                };
              })
            : groups,
        ruleOptions: mergeAutomationRuleOptions(
          entityType,
          harvest,
          matchTypes,
          portfolioScope ? scopeKind : undefined
        ),
        status: nextStatus,
      };
      if (initial?.id) {
        await automationApi.updateRule(profileId, initial.id, body);
        return { created: false, enabled: nextStatus === 'active' };
      }
      await automationApi.createRule(profileId, body);
      return { created: true, enabled: nextStatus === 'active' };
    },
    onSuccess: (result) => {
      if (result.created && result.enabled) {
        showToast('Rule created and enabled', 'success');
      } else if (result.created) {
        showToast('Rule created (paused). Enable it to start the schedule.', 'success');
      } else {
        showToast(result.enabled ? 'Rule updated' : 'Rule updated (still paused)', 'success');
      }
      void qc.invalidateQueries({ queryKey: ['automation-rules', profileId] });
      onClose();
    },
    onError: (e: unknown) => {
      showToast(e instanceof Error ? e.message : 'Save failed', 'error');
    },
  });

  const summaryLines = useMemo(() => groups.map(formatConditionGroupLine), [groups]);

  const metrics = (metricsQ.data ?? []) as AutomationMetric[];
  const actions = (actionsQ.data ?? []).filter((a) => a.enabled);
  const sbOnly = adTypes.length === 1 && adTypes[0] === 'SB';
  const endDateValid = noEndDate || (Boolean(endDate) && endDate >= startDate);
  const canSave =
    Boolean(name.trim()) &&
    Boolean(startDate) &&
    endDateValid &&
    !saveMut.isPending &&
    (effectiveScope === 'all' || selectedEntities.length > 0) &&
    (frequency === 'hourly' || executionHours.length > 0) &&
    (entityType !== 'placement' ||
      groups.every((g) =>
        placementActionHasAdjustments({
          type: 'placement_value',
          adjustments: serializePlacementAdjustments(parsePlacementAdjustmentRows(g.action), {
            disableTopOfSearch: sbOnly,
          }),
        })
      ));

  return (
    <div className="fixed inset-0 z-[80] flex items-start justify-center overflow-y-auto bg-black/50 p-4 pt-10">
      <div className="w-full max-w-5xl rounded-xl bg-surface shadow-xl">
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <h2 className="text-lg font-semibold text-foreground">
            {initial ? 'Edit rule' : 'Create automation rule'}
            <span className="ml-2 rounded-full bg-surface-muted px-2 py-0.5 text-xs font-normal text-foreground-muted">
              {entityTypeLabel(entityType)}
            </span>
          </h2>
          <button type="button" className="text-foreground-muted hover:text-foreground" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="grid gap-6 p-5 md:grid-cols-[1fr_220px]">
          <div className="space-y-4">
            <label className="block text-sm">
              <span className="mb-1 block text-foreground-muted">Rule name</span>
              <input
                className="w-full rounded border border-border px-3 py-2"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block text-sm">
                <span className="mb-1 block text-foreground-muted">Start date</span>
                <input
                  type="date"
                  className="w-full rounded border border-border px-3 py-2"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </label>
              <div className="block text-sm">
                <span className="mb-1 flex items-center justify-between gap-2 text-foreground-muted">
                  End date
                  <label className="flex items-center gap-1.5 text-xs font-normal text-foreground">
                    <input
                      type="checkbox"
                      checked={noEndDate}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setNoEndDate(checked);
                        if (checked) setEndDate('');
                      }}
                    />
                    No end date
                  </label>
                </span>
                <input
                  type="date"
                  className="w-full rounded border border-border px-3 py-2 disabled:cursor-not-allowed disabled:opacity-50"
                  value={noEndDate ? '' : endDate}
                  min={startDate || undefined}
                  disabled={noEndDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
                {!noEndDate && endDate && startDate && endDate < startDate && (
                  <span className="mt-1 block text-xs text-error-ink">
                    End date must be on or after the start date
                  </span>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {(['SP', 'SB', 'SD'] as const).map((p) => {
                const allowed = allowedAdTypes.includes(p);
                return (
                <button
                  key={p}
                  type="button"
                  disabled={!allowed}
                  className={cn(
                    'rounded border px-3 py-1 text-sm',
                    !allowed && 'cursor-not-allowed opacity-40',
                    adTypes.includes(p) ? 'border-brand bg-brand/10 text-brand' : 'border-border'
                  )}
                  onClick={() => {
                    if (!allowed) return;
                    setAdTypes((prev) => {
                      const next = prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p];
                      return next.length ? next : prev;
                    });
                  }}
                >
                  {p === 'SP' ? 'Sponsored Products' : p === 'SB' ? 'Sponsored Brands' : 'Sponsored Display'}
                </button>
              );
              })}
            </div>
            {entityType === 'target' && (
              <div className="space-y-2">
                <div className="text-sm font-medium">Match type</div>
                <div className="flex flex-wrap gap-2">
                  {TARGET_MATCH_TYPES.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      className={cn(
                        'rounded border px-3 py-1 text-sm',
                        matchTypes.includes(item.id)
                          ? 'border-brand bg-brand/10 text-brand'
                          : 'border-border'
                      )}
                      onClick={() => {
                        setMatchTypes((prev) => {
                          const next = prev.includes(item.id)
                            ? prev.filter((id) => id !== item.id)
                            : [...prev, item.id];
                          return next.length ? next : prev;
                        });
                      }}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="space-y-2">
              <div className="text-sm font-medium">{copy.heading}</div>
              <div className="flex flex-wrap gap-4 text-sm">
                {portfolioScope ? (
                  <>
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        checked={scopeKind === 'all'}
                        onChange={() => setScopeKind('all')}
                      />
                      {copy.all}
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        checked={scopeKind === 'campaigns'}
                        onChange={() => {
                          if (scopeKind === 'ad_groups' || scopeKind === 'portfolios') {
                            setSelectedEntities([]);
                          }
                          setScopeKind('campaigns');
                        }}
                      />
                      {copy.specific}
                    </label>
                    {splitScope ? (
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          checked={scopeKind === 'ad_groups'}
                          onChange={() => {
                            if (scopeKind === 'campaigns' || scopeKind === 'portfolios') {
                              setSelectedEntities([]);
                            }
                            setScopeKind('ad_groups');
                          }}
                        />
                        {copy.specificAdGroups}
                      </label>
                    ) : null}
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        checked={scopeKind === 'portfolios'}
                        onChange={() => {
                          if (scopeKind === 'campaigns' || scopeKind === 'ad_groups') {
                            setSelectedEntities([]);
                          }
                          setScopeKind('portfolios');
                        }}
                      />
                      {copy.portfolio || 'Portfolio'}
                    </label>
                  </>
                ) : (
                  <>
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        checked={entityScope === 'all'}
                        onChange={() => setEntityScope('all')}
                      />
                      {copy.all}
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        checked={entityScope === 'specific'}
                        onChange={() => setEntityScope('specific')}
                      />
                      {copy.specific}
                    </label>
                  </>
                )}
              </div>
              {effectiveScope === 'specific' && (
                <AutomationCampaignPicker
                  options={entitiesQ.data ?? []}
                  selected={selectedEntities}
                  onChange={setSelectedEntities}
                  isLoading={entitiesQ.isLoading}
                  searchPlaceholder={pickerSearch}
                  loadingLabel={`Loading ${pickerNoun}…`}
                  emptyLabel={`No ${pickerNoun} match.`}
                  countNoun={pickerNoun}
                  showAdProduct={scopeKind !== 'portfolios'}
                />
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <label className="block text-sm">
                <span className="mb-1 block text-foreground-muted">Lookback days</span>
                <input
                  type="number"
                  className="w-full rounded border border-border px-3 py-2"
                  value={lookbackDays}
                  onChange={(e) => setLookbackDays(Number(e.target.value))}
                />
              </label>
              <label className="block text-sm">
                <span className="mb-1 block text-foreground-muted">Exclude days</span>
                <input
                  type="number"
                  className="w-full rounded border border-border px-3 py-2"
                  value={excludeDays}
                  onChange={(e) => setExcludeDays(Number(e.target.value))}
                />
              </label>
            </div>
            <div className="space-y-3 rounded border border-border p-3">
              <div className="text-sm font-medium">Conditions & actions</div>
              {groups.map((g, gi) => (
                <div key={gi} className="space-y-2 rounded bg-surface-muted p-3">
                  <div className="text-xs uppercase text-foreground-muted">{g.kind}</div>
                  {g.kind !== 'else' && (
                    <div className="space-y-2">
                      {(g.conditions?.children ?? []).map((leaf, li) => (
                        <div key={li} className="space-y-2">
                          {li > 0 && (
                            <div className="text-xs font-medium uppercase text-foreground-muted">AND</div>
                          )}
                          <div className={CONDITION_ROW_GRID}>
                            <AutomationMetricSelect
                              metrics={metrics}
                              value={leaf.metric}
                              onChange={(nextKey) => {
                                const def = metrics.find((m) => m.key === nextKey);
                                const ops = operatorsForMetric(def);
                                setGroups((prev) =>
                                  updateGroupChildren(prev, gi, (children) =>
                                    children.map((c, j) =>
                                      j === li
                                        ? {
                                            ...c,
                                            metric: nextKey,
                                            operator: ops[0]?.value ?? 'gt',
                                            value_type: 'fixed',
                                            value: defaultMetricValue(def),
                                          }
                                        : c
                                    )
                                  )
                                );
                              }}
                            />
                            <select
                              className="min-w-0 rounded border border-border px-2 py-1.5 text-sm"
                              value={leaf.operator}
                              onChange={(e) =>
                                setGroups((prev) =>
                                  updateGroupChildren(prev, gi, (children) =>
                                    children.map((c, j) =>
                                      j === li ? { ...c, operator: e.target.value } : c
                                    )
                                  )
                                )
                              }
                              aria-label="Comparison operator"
                            >
                              {operatorsForMetric(metrics.find((m) => m.key === leaf.metric)).map((o) => (
                                <option key={o.value} value={o.value}>
                                  {o.symbol} {o.label}
                                </option>
                              ))}
                            </select>
                            {isDateMetric(metrics.find((m) => m.key === leaf.metric), leaf.metric) ? (
                              <input
                                type="date"
                                className="min-w-0 rounded border border-border px-2 py-1.5 text-sm"
                                value={String(leaf.value || todayIso())}
                                onChange={(e) =>
                                  setGroups((prev) =>
                                    updateGroupChildren(prev, gi, (children) =>
                                      children.map((c, j) =>
                                        j === li ? { ...c, value: e.target.value } : c
                                      )
                                    )
                                  )
                                }
                                aria-label="Date value"
                              />
                            ) : isEnumMetric(metrics.find((m) => m.key === leaf.metric), leaf.metric) ? (
                              <select
                                className="min-w-0 rounded border border-border px-2 py-1.5 text-sm"
                                value={String(leaf.value || (leaf.metric === 'match_type' ? 'EXACT' : 'ENABLED'))}
                                onChange={(e) =>
                                  setGroups((prev) =>
                                    updateGroupChildren(prev, gi, (children) =>
                                      children.map((c, j) =>
                                        j === li ? { ...c, value: e.target.value } : c
                                      )
                                    )
                                  )
                                }
                                aria-label={leaf.metric === 'match_type' ? 'Match type value' : 'Status value'}
                              >
                                {leaf.metric === 'match_type'
                                  ? TARGET_MATCH_TYPES.map((item) => (
                                      <option key={item.id} value={item.id}>
                                        {item.label}
                                      </option>
                                    ))
                                  : (
                                    <>
                                      <option value="ENABLED">Enabled</option>
                                      <option value="PAUSED">Paused</option>
                                    </>
                                  )}
                              </select>
                            ) : (
                              <input
                                type="number"
                                className="min-w-0 rounded border border-border px-2 py-1.5 text-sm"
                                value={leaf.value}
                                onChange={(e) =>
                                  setGroups((prev) =>
                                    updateGroupChildren(prev, gi, (children) =>
                                      children.map((c, j) =>
                                        j === li ? { ...c, value: Number(e.target.value) } : c
                                      )
                                    )
                                  )
                                }
                              />
                            )}
                            {li > 0 ? (
                              <button
                                type="button"
                                className="text-foreground-muted hover:text-foreground"
                                aria-label="Remove condition"
                                onClick={() =>
                                  setGroups((prev) =>
                                    updateGroupChildren(prev, gi, (children) =>
                                      children.filter((_, j) => j !== li)
                                    )
                                  )
                                }
                              >
                                ✕
                              </button>
                            ) : (
                              <span />
                            )}
                          </div>
                        </div>
                      ))}
                      {(g.conditions?.children?.length ?? 0) < MAX_AND_CONDITIONS && (
                        <button
                          type="button"
                          className="text-sm text-brand"
                          onClick={() =>
                            setGroups((prev) =>
                              updateGroupChildren(prev, gi, (children) => [
                                ...children,
                                emptyConditionLeaf(metrics),
                              ])
                            )
                          }
                        >
                          + AND
                        </button>
                      )}
                    </div>
                  )}
                  {entityType === 'placement' ? (
                    <PlacementValueActionFields
                      action={g.action}
                      disableTopOfSearch={sbOnly}
                      onChange={(next) => {
                        const copy = [...groups];
                        copy[gi] = {
                          ...copy[gi],
                          action: preserveRevertAtDayEnd(
                            next,
                            copy[gi].action.revertAtDayEnd,
                            catalogActionIsRevertible(next.type, actions),
                            copy[gi].action.revertValue
                          ),
                        };
                        setGroups(copy);
                      }}
                    />
                  ) : entityType === 'target' ? (
                    <TargetActionFields
                      action={g.action}
                      onChange={(next) => {
                        const copy = [...groups];
                        copy[gi] = {
                          ...copy[gi],
                          action: preserveRevertAtDayEnd(
                            next,
                            copy[gi].action.revertAtDayEnd,
                            catalogActionIsRevertible(next.type, actions),
                            copy[gi].action.revertValue
                          ),
                        };
                        setGroups(copy);
                      }}
                    />
                  ) : (
                    <div className="grid grid-cols-[minmax(0,1.6fr)_minmax(0,0.8fr)] gap-2">
                      <select
                        className="min-w-0 rounded border border-border px-2 py-1.5 text-sm"
                        value={g.action.type}
                        onChange={(e) => {
                          const type = e.target.value;
                          const next = groups.map((group, index) => {
                            if (index !== gi) return group;
                            return {
                              ...group,
                              action: preserveRevertAtDayEnd(
                                { ...group.action, type },
                                group.action.revertAtDayEnd,
                                catalogActionIsRevertible(type, actions),
                                group.action.revertValue
                              ),
                            };
                          });
                          setGroups(next);
                        }}
                      >
                        {actions.map((a) => (
                          <option key={a.type} value={a.type}>
                            {a.label}
                          </option>
                        ))}
                      </select>
                      {!['pause_campaign', 'enable_campaign', 'send_alert'].includes(g.action.type) && (
                        <input
                          type="number"
                          className="min-w-0 rounded border border-border px-2 py-1.5 text-sm"
                          value={g.action.value ?? 0}
                          onChange={(e) => {
                            const next = [...groups];
                            next[gi].action.value = Number(e.target.value);
                            setGroups(next);
                          }}
                        />
                      )}
                    </div>
                  )}
                  {catalogActionIsRevertible(g.action.type, actions) ? (
                    <div className="space-y-2">
                      <RevertAtDayEndSwitch
                        checked={Boolean(g.action.revertAtDayEnd)}
                        onChange={(on) => {
                          const next = groups.map((group, index) => {
                            if (index !== gi) return group;
                            return {
                              ...group,
                              action: { ...group.action, revertAtDayEnd: on },
                            };
                          });
                          setGroups(next);
                        }}
                      />
                      {g.action.revertAtDayEnd && actionUsesNumericRestore(g.action.type) ? (
                        <label className="block text-sm">
                          <span className="mb-1 block text-foreground-muted">Restore to</span>
                          <input
                            type="number"
                            className="w-full max-w-[8rem] rounded border border-border px-2 py-1.5 text-sm"
                            aria-label="Restore to"
                            placeholder="Optional"
                            value={g.action.revertValue ?? ''}
                            onChange={(e) => {
                              const raw = e.target.value;
                              const next = groups.map((group, index) => {
                                if (index !== gi) return group;
                                return {
                                  ...group,
                                  action: {
                                    ...group.action,
                                    revertValue: raw === '' ? undefined : Number(raw),
                                  },
                                };
                              });
                              setGroups(next);
                            }}
                          />
                        </label>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              ))}
              {groups.length < 5 && (
                <button
                  type="button"
                  className="text-sm text-brand"
                  onClick={() =>
                    setGroups((prev) => [
                      ...prev,
                      prev.length === 0
                        ? (defaultConditionGroups(entityType)[0] as ConditionGroup)
                        : {
                            ...(defaultConditionGroups(entityType)[0] as ConditionGroup),
                            kind: 'else_if',
                          },
                    ])
                  }
                >
                  + Add ELSE IF
                </button>
              )}
            </div>
            {entityType === 'st_harvest' && (
              <div className="space-y-2 rounded border border-border p-3">
                <div className="text-sm font-medium">Harvest options</div>
                <label className="block text-sm">
                  <span className="mb-1 block text-foreground-muted">Match type</span>
                  <select
                    className="w-full rounded border border-border px-3 py-2"
                    value={harvest.matchType}
                    onChange={(e) => setHarvest((h) => ({ ...h, matchType: e.target.value }))}
                  >
                    <option value="EXACT">Exact</option>
                    <option value="PHRASE">Phrase</option>
                    <option value="BROAD">Broad</option>
                  </select>
                </label>
                <label className="block text-sm">
                  <span className="mb-1 block text-foreground-muted">Starting bid</span>
                  <select
                    className="w-full rounded border border-border px-3 py-2"
                    value={harvest.bidSource}
                    onChange={(e) =>
                      setHarvest((h) => ({
                        ...h,
                        bidSource: e.target.value as HarvestOptions['bidSource'],
                      }))
                    }
                  >
                    <option value="ad_group_default">Ad group default</option>
                    <option value="source_keyword">Source keyword bid</option>
                    <option value="fixed">Fixed bid</option>
                  </select>
                </label>
                {harvest.bidSource === 'fixed' && (
                  <label className="block text-sm">
                    <span className="mb-1 block text-foreground-muted">Bid</span>
                    <input
                      type="number"
                      className="w-full rounded border border-border px-3 py-2"
                      value={harvest.bid ?? ''}
                      onChange={(e) => setHarvest((h) => ({ ...h, bid: Number(e.target.value) }))}
                    />
                  </label>
                )}
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={harvest.skipIfExists}
                    onChange={(e) => setHarvest((h) => ({ ...h, skipIfExists: e.target.checked }))}
                  />
                  Skip if keyword already exists
                </label>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <label className="block text-sm">
                <span className="mb-1 block text-foreground-muted">Frequency</span>
                <select
                  className="w-full rounded border border-border px-3 py-2"
                  value={frequency}
                  onChange={(e) => setFrequency(e.target.value)}
                >
                  <option value="hourly">Hourly</option>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                </select>
              </label>
              <label className="block text-sm">
                <span className="mb-1 block text-foreground-muted">Timezone</span>
                <select
                  className="w-full rounded border border-border px-3 py-2"
                  value={timezone}
                  onChange={(e) => setTimezone(e.target.value)}
                >
                  <option value="Asia/Kolkata">IST</option>
                  <option value="America/Los_Angeles">PST</option>
                  <option value="UTC">UTC</option>
                </select>
              </label>
            </div>
            {frequency !== 'hourly' && (
              <div>
                <span className="mb-1 block text-sm text-foreground-muted">Hours</span>
                <div className="flex flex-wrap gap-1">
                  {Array.from({ length: 24 }, (_, h) => (
                    <button
                      key={h}
                      type="button"
                      className={cn(
                        'rounded border px-2 py-1 text-xs',
                        executionHours.includes(h) ? 'border-brand bg-brand/10' : 'border-border'
                      )}
                      onClick={() =>
                        setExecutionHours((prev) => {
                          if (prev.includes(h)) {
                            if (prev.length === 1) return prev;
                            return prev.filter((x) => x !== h);
                          }
                          return [...prev, h].sort((a, b) => a - b);
                        })
                      }
                    >
                      {formatHourLabel(h)}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {frequency === 'weekly' && (
              <div>
                <span className="mb-1 block text-sm text-foreground-muted">Days</span>
                <div className="flex flex-wrap gap-1">
                  {[1, 2, 3, 4, 5, 6, 7].map((d) => (
                    <button
                      key={d}
                      type="button"
                      className={cn(
                        'rounded border px-2 py-1 text-xs',
                        weeklyDays.includes(d) ? 'border-brand bg-brand/10' : 'border-border'
                      )}
                      onClick={() =>
                        setWeeklyDays((prev) =>
                          prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]
                        )
                      }
                    >
                      {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][d - 1]}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div>
              <div className="flex gap-4 text-sm">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    checked={executionMode === 'automatic'}
                    onChange={() => setExecutionMode('automatic')}
                  />
                  Automatic
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    checked={executionMode === 'manual'}
                    onChange={() => setExecutionMode('manual')}
                  />
                  Manual
                </label>
              </div>
              {executionMode === 'manual' && (
                <p className="mt-2 text-xs text-foreground-muted">
                  Everyone with access to this account will get an email when this rule runs.
                  Actions stay in Pending logs until they are confirmed.
                </p>
              )}
            </div>
          </div>
          <aside className="rounded border border-border bg-surface-muted p-3 text-sm">
            <div className="mb-2 font-medium">Rule summary</div>
            <ul className="space-y-2 text-foreground-muted">
              {summaryLines.map((line, i) => (
                <li key={i}>{line}</li>
              ))}
            </ul>
            <div className="mt-3 text-xs text-foreground-muted">
              {ruleScopeHint(effectiveScope, selectedEntities.length, pickerNoun)} · Lookback {lookbackDays}d
              excl. {excludeDays} · {frequency}
              {frequency !== 'hourly' && executionHours.length > 0
                ? ` · ${executionHours.map(formatHourLabel).join(', ')}`
                : ''}
            </div>
          </aside>
        </div>
        <div className="flex justify-end gap-2 border-t border-border px-5 py-4">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="secondary"
            disabled={!canSave}
            onClick={() => saveMut.mutate({ enable: false })}
          >
            {saveMut.isPending ? 'Saving…' : initial ? 'Save rule' : 'Save paused'}
          </Button>
          {!initial && (
            <Button disabled={!canSave} onClick={() => saveMut.mutate({ enable: true })}>
              {saveMut.isPending ? 'Saving…' : 'Save & enable'}
            </Button>
          )}
          {initial && initial.status !== 'active' && (
            <Button disabled={!canSave} onClick={() => saveMut.mutate({ enable: true })}>
              {saveMut.isPending ? 'Saving…' : 'Save & enable'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function catalogFromMetrics(
  rows: Array<Record<string, unknown>> | undefined
): ConditionMetricMeta[] {
  if (!rows) return [];
  return rows.flatMap((row) => {
    const key = typeof row.key === 'string' ? row.key : '';
    if (!key) return [];
    const label = typeof row.label === 'string' && row.label.trim() ? row.label : key;
    const unit = typeof row.unit === 'string' ? row.unit : undefined;
    return [{ key, label, unit }];
  });
}

function ExecutionValueLines({ value }: { value: unknown }) {
  const lines = executionValueLines(value);
  if (lines.length <= 1) {
    return <span className="whitespace-normal">{lines[0] ?? '—'}</span>;
  }
  return (
    <div className="flex min-w-[11rem] flex-col gap-0.5 whitespace-normal">
      {lines.map((line, index) => (
        <span key={`${line}-${index}`}>{line}</span>
      ))}
    </div>
  );
}

function ExecutionRunDetailModal({
  profileId,
  run,
  onClose,
}: {
  profileId: string;
  run: AutomationExecutionRun;
  onClose: () => void;
}) {
  const entityLabel = entityNoun(run.entityType);
  const isPlacement = run.entityType === 'placement';
  const metricsCatalogQ = useQuery({
    queryKey: ['automation-meta', profileId, 'metrics', 'placement'],
    queryFn: () => automationApi.listMetrics(profileId, 'placement').then((r) => r.data ?? []),
    enabled: isPlacement,
  });
  const metricCatalog = useMemo(
    () => catalogFromMetrics(metricsCatalogQ.data),
    [metricsCatalogQ.data]
  );
  const itemsQ = useQuery({
    queryKey: ['automation-executions', profileId, run.ruleId, run.scheduledSlot],
    queryFn: () =>
      automationApi
        .listExecutions(profileId, {
          rule_id: run.ruleId,
          scheduled_slot: run.scheduledSlot || undefined,
          limit: 2000,
        })
        .then((r) => r.data),
  });
  const items = itemsQ.data?.items ?? [];
  const change = summarizeGroupChange(
    items.filter((row) => row.status !== 'skipped_conditions')
  );

  return (
    <div
      className="fixed inset-0 z-[80] flex items-start justify-center overflow-y-auto bg-black/50 p-4 pt-10"
      onClick={onClose}
    >
      <div
        className={cn(
          'w-full rounded-xl bg-surface shadow-xl',
          isPlacement ? 'max-w-[96rem]' : 'max-w-5xl'
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <div>
            <h2 className="text-lg font-semibold text-foreground">
              {ruleLabel(run.ruleName, run.ruleId)}
            </h2>
            <p className="mt-0.5 text-sm text-foreground-muted">
              {run.total} {entityLabel}
              {run.executionTime
                ? ` · ${new Date(run.executionTime).toLocaleString()}`
                : ''}
              {run.dryRun ? ' · dry run' : ''}
              {change.action !== '—' ? ` · ${change.action}` : ''}
            </p>
          </div>
          <button type="button" className="text-foreground-muted hover:text-foreground" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="p-5">
          {itemsQ.isLoading ? (
            <Spinner />
          ) : (
            <TableWrap>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Entity</TableHead>
                    <TableHead>Condition vs actual</TableHead>
                    {isPlacement ? <TableHead>Metrics</TableHead> : null}
                    <TableHead>Action</TableHead>
                    <TableHead>Before</TableHead>
                    <TableHead>After</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Revert</TableHead>
                    <TableHead>Error</TableHead>
                  </TableRow>
                </TableHeader>
                <tbody>
                  {items.map((row) => {
                    const unmatched = row.status === 'skipped_conditions';
                    return (
                      <TableRow key={row.id}>
                        <TableCell className="font-medium">{row.entityName || row.entityKey}</TableCell>
                        <TableCell
                          className={
                            isPlacement
                              ? 'align-top text-xs text-foreground-muted'
                              : 'max-w-[280px] text-xs text-foreground-muted'
                          }
                        >
                          {isPlacement ? (
                            <PlacementConditionCell
                              evaluation={row.evaluation}
                              evaluationSummary={row.evaluationSummary}
                              catalog={metricCatalog}
                            />
                          ) : (
                            formatConditionColumnText(
                              row.evaluationSummary || formatEvaluationSummary(row.evaluation)
                            )
                          )}
                        </TableCell>
                        {isPlacement ? (
                          <TableCell className="align-top">
                            <PlacementMetricsCell
                              current={row.evaluation?.placementMetrics}
                              previous={row.evaluation?.placementMetricsPrevious}
                            />
                          </TableCell>
                        ) : null}
                        <TableCell className="text-xs text-foreground-muted">
                          {unmatched ? '—' : formatActionType(row.actionType)}
                        </TableCell>
                        <TableCell
                          className={
                            isPlacement
                              ? 'align-top text-xs'
                              : 'max-w-[180px] truncate text-xs'
                          }
                        >
                          {unmatched ? (
                            '—'
                          ) : isPlacement ? (
                            <ExecutionValueLines value={row.beforeValue} />
                          ) : (
                            formatExecutionValue(row.beforeValue)
                          )}
                        </TableCell>
                        <TableCell
                          className={
                            isPlacement
                              ? 'align-top text-xs'
                              : 'max-w-[180px] truncate text-xs'
                          }
                        >
                          {unmatched ? (
                            '—'
                          ) : isPlacement ? (
                            <ExecutionValueLines value={row.afterValue} />
                          ) : (
                            formatExecutionValue(row.afterValue)
                          )}
                        </TableCell>
                        <TableCell>
                          <span
                            className={cn(
                              'rounded-full border px-2 py-0.5 text-xs',
                              statusBadgeClass(row.status)
                            )}
                          >
                            {formatExecutionStatus(row.status)}
                          </span>
                        </TableCell>
                        <TableCell className="text-xs text-foreground-muted">
                          {formatRevertStatus(row.revertStatus)}
                        </TableCell>
                        <TableCell className="max-w-[180px] truncate text-xs text-error-ink">
                          {row.errorMessage || row.revertError || '—'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {items.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={isPlacement ? 9 : 8} className="py-8 text-center text-foreground-muted">
                        No executions in this run
                      </TableCell>
                    </TableRow>
                  )}
                </tbody>
              </Table>
            </TableWrap>
          )}
        </div>
        <div className="flex justify-end gap-2 border-t border-border px-5 py-4">
          <Button
            variant="secondary"
            disabled={items.length === 0}
            onClick={() =>
              downloadAutomationExecutionsCsv(items, { ruleId: run.ruleId })
            }
          >
            <Download className="w-3.5 h-3.5" />
            Export CSV
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}

function PendingRunDetailModal({
  profileId,
  run,
  canExecute,
  busy,
  onClose,
  onConfirmOne,
  onCancelOne,
  onConfirmAll,
  onCancelAll,
}: {
  profileId: string;
  run: AutomationPendingRun;
  canExecute: boolean;
  busy: boolean;
  onClose: () => void;
  onConfirmOne: (id: string) => void;
  onCancelOne: (id: string) => void;
  onConfirmAll: (ids: string[]) => void;
  onCancelAll: (ids: string[]) => void;
}) {
  const entityLabel = run.entityNoun || entityNoun(run.entityType);
  const isPlacement = run.entityType === 'placement';
  const itemsQ = useQuery({
    queryKey: ['automation-pending', profileId, 'run', run.ruleId, run.scheduledSlot],
    queryFn: () =>
      fetchAllPendingForRun({
        profileId,
        ruleId: run.ruleId,
        scheduledSlot: run.scheduledSlot,
      }),
  });
  const items = itemsQ.data ?? [];
  const actionable = items.filter((row) => row.status === 'pending');
  const change = summarizeGroupChange(items.map(pendingRowToChange));
  const shownCount = itemsQ.isSuccess ? items.length : run.total;
  const actionableIds = actionable.map((row) => row.id);

  return (
    <div
      className="fixed inset-0 z-[80] flex items-start justify-center overflow-y-auto bg-black/50 p-4 pt-10"
      onClick={onClose}
    >
      <div
        className={cn(
          'w-full rounded-xl bg-surface shadow-xl',
          isPlacement ? 'max-w-[96rem]' : 'max-w-5xl'
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <div>
            <h2 className="text-lg font-semibold text-foreground">
              {ruleLabel(run.ruleName, run.ruleId)}
            </h2>
            <p className="mt-0.5 text-sm text-foreground-muted">
              {shownCount} {entityLabel}
              {run.scheduledSlot ? ` · ${new Date(run.scheduledSlot).toLocaleString()}` : ''}
              {change.action !== '—' ? ` · ${change.action}` : ''}
            </p>
          </div>
          <button type="button" className="text-foreground-muted hover:text-foreground" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="p-5">
          {itemsQ.isLoading ? (
            <Spinner />
          ) : itemsQ.isError ? (
            <p className="text-sm text-foreground-muted">
              {itemsQ.error instanceof Error
                ? itemsQ.error.message
                : 'Failed to load pending actions'}
            </p>
          ) : (
          <TableWrap>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Entity</TableHead>
                  {isPlacement ? <TableHead>Metrics</TableHead> : null}
                  <TableHead>Action</TableHead>
                  <TableHead>Before</TableHead>
                  <TableHead>After</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <tbody>
                {items.map((row) => {
                  const change = pendingRowToChange(row);
                  const evaluation = isPlacement ? pendingEvaluation(row) : null;
                  return (
                    <TableRow key={row.id}>
                      <TableCell className={cn('font-medium', isPlacement && 'align-top')}>
                        {row.entityName || row.entityKey}
                      </TableCell>
                      {isPlacement ? (
                        <TableCell className="align-top">
                          <PlacementMetricsCell
                            current={evaluation?.placementMetrics}
                            previous={evaluation?.placementMetricsPrevious}
                          />
                        </TableCell>
                      ) : null}
                      <TableCell
                        className={cn(
                          'text-xs text-foreground-muted',
                          isPlacement && 'align-top'
                        )}
                      >
                        {formatActionType(change.actionType || pendingActionType(row))}
                      </TableCell>
                      <TableCell
                        className={
                          isPlacement ? 'align-top text-xs' : 'max-w-[180px] truncate text-xs'
                        }
                      >
                        {isPlacement ? (
                          <ExecutionValueLines value={change.beforeValue} />
                        ) : (
                          formatExecutionValue(change.beforeValue)
                        )}
                      </TableCell>
                      <TableCell
                        className={
                          isPlacement ? 'align-top text-xs' : 'max-w-[180px] truncate text-xs'
                        }
                      >
                        {isPlacement ? (
                          <ExecutionValueLines value={change.afterValue} />
                        ) : (
                          formatExecutionValue(change.afterValue)
                        )}
                      </TableCell>
                      <TableCell className={isPlacement ? 'align-top' : undefined}>
                        <span
                          className={cn(
                            'rounded-full border px-2 py-0.5 text-xs',
                            statusBadgeClass(row.status)
                          )}
                        >
                          {row.status}
                        </span>
                      </TableCell>
                      <TableCell className={isPlacement ? 'align-top' : undefined}>
                        {row.status === 'pending' && canExecute && (
                          <div className="flex gap-2">
                            <button
                              type="button"
                              className="text-sm text-brand disabled:opacity-50"
                              disabled={busy}
                              onClick={() => onConfirmOne(row.id)}
                            >
                              Confirm
                            </button>
                            <button
                              type="button"
                              className="text-sm text-foreground-muted disabled:opacity-50"
                              disabled={busy}
                              onClick={() => onCancelOne(row.id)}
                            >
                              Cancel
                            </button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </tbody>
            </Table>
          </TableWrap>
          )}
        </div>
        <div className="flex justify-end gap-2 border-t border-border px-5 py-4">
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Close
          </Button>
          {canExecute && itemsQ.isSuccess && actionableIds.length > 0 && (
            <>
              <Button
                variant="secondary"
                disabled={busy}
                onClick={() => onCancelAll(actionableIds)}
              >
                {busy ? 'Working…' : `Cancel all (${actionableIds.length})`}
              </Button>
              <Button disabled={busy} onClick={() => onConfirmAll(actionableIds)}>
                {busy ? 'Working…' : `Confirm all (${actionableIds.length})`}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export function AutomationsPage() {
  const { profileId: rawProfileId } = useParams();
  const profileId = rawProfileId ? decodeURIComponent(rawProfileId) : '';
  const [searchParams, setSearchParams] = useSearchParams();
  const nav = parseAutomationsNav(searchParams);
  const tab = nav.tab;
  const entityType = (
    isAutomationRuleEntity(nav.entity) ? nav.entity : 'campaign'
  ) as AutomationEntityType;
  const ruleFilter = nav.rule;
  const { hasPermission } = useAuth();
  const { showToast } = useToast();
  const qc = useQueryClient();
  const canWrite = hasPermission('automation.write');
  const canExecute = hasPermission('automation.execute');
  const [builderOpen, setBuilderOpen] = useState(false);
  const [editRule, setEditRule] = useState<AutomationRule | null>(null);
  const [search, setSearch] = useState('');
  const [selectedExecRun, setSelectedExecRun] = useState<AutomationExecutionRun | null>(null);
  const [selectedPendingRun, setSelectedPendingRun] = useState<AutomationPendingRun | null>(null);
  const [execOutcome, setExecOutcome] = useState<'all' | AutomationExecutionOutcome>('all');
  const [daypartingKind, setDaypartingKind] = useState<'budget' | 'bid' | 'placement'>('budget');

  const setTabParams = (next: {
    tab: string;
    entity?: string;
    rule?: string;
    section?: string;
  }) => {
    setSearchParams(buildAutomationsSearchParams(nav, next));
  };

  const rulesQ = useQuery({
    queryKey: ['automation-rules', profileId, entityType, search],
    enabled: Boolean(profileId) && tab === 'rules' && entityType !== 'term_list',
    queryFn: () =>
      automationApi
        .listRules(profileId, { entity_type: entityType, q: search || undefined })
        .then((r) => r.data),
  });
  const filterRulesQ = useQuery({
    queryKey: ['automation-rules-filter', profileId],
    enabled: Boolean(profileId) && (tab === 'pending' || tab === 'executed'),
    queryFn: () =>
      automationApi.listRules(profileId, { limit: 200 }).then((r) => r.data),
  });
  const pendingRunsQ = useQuery({
    queryKey: ['automation-pending-runs', profileId, ruleFilter],
    enabled: Boolean(profileId) && tab === 'pending',
    queryFn: () =>
      automationApi
        .listPendingRuns(profileId, { rule_id: ruleFilter || undefined })
        .then((r) => r.data),
  });
  const execOutcomeParam = execOutcome === 'all' ? undefined : execOutcome;
  const execQ = useQuery({
    queryKey: ['automation-execution-runs', profileId, ruleFilter, execOutcomeParam],
    enabled: Boolean(profileId) && tab === 'executed',
    queryFn: () =>
      automationApi
        .listExecutionRuns(profileId, {
          rule_id: ruleFilter || undefined,
          outcome: execOutcomeParam,
        })
        .then((r) => r.data),
  });

  const exportExecutions = useCallback(async () => {
    try {
      const fetched = await fetchAllAutomationExecutions({
        profileId,
        ruleId: ruleFilter || undefined,
        outcome: execOutcomeParam,
      });
      downloadAutomationExecutionsCsv(fetched.items, {
        ruleId: ruleFilter || undefined,
      });
      if (fetched.truncated) {
        showToast(
          `Exported first ${fetched.items.length.toLocaleString()} of ${fetched.total.toLocaleString()} executions`,
          'error'
        );
      } else {
        showToast(`Exported ${fetched.items.length.toLocaleString()} executions`, 'success');
      }
    } catch (error) {
      showToast(
        error instanceof Error ? error.message : 'Failed to export executions',
        'error'
      );
    }
  }, [profileId, ruleFilter, execOutcomeParam, showToast]);

  const pendingRuns = useMemo(
    () => pendingRunsQ.data?.items ?? [],
    [pendingRunsQ.data]
  );

  useEffect(() => {
    setSelectedPendingRun((prev) => {
      if (!prev) return null;
      return (
        pendingRuns.find(
          (run) => run.ruleId === prev.ruleId && run.scheduledSlot === prev.scheduledSlot
        ) ?? null
      );
    });
  }, [pendingRuns]);

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['automation-rules', profileId] });
    void qc.invalidateQueries({ queryKey: ['automation-rules-filter', profileId] });
    void qc.invalidateQueries({ queryKey: ['automation-pending', profileId] });
    void qc.invalidateQueries({ queryKey: ['automation-pending-runs', profileId] });
    void qc.invalidateQueries({ queryKey: ['automation-executions', profileId] });
    void qc.invalidateQueries({ queryKey: ['automation-execution-runs', profileId] });
  };

  const refreshCurrentTab = () => {
    if (tab === DAYPARTING_TAB) {
      void qc.invalidateQueries({ queryKey: ['profile-hourly-budgets', profileId] });
      return;
    }
    if (tab === AUTOMATIONS_TAB_BID_SNAPSHOTS) {
      void qc.invalidateQueries({ queryKey: ['bid-snapshots', profileId] });
      void qc.invalidateQueries({ queryKey: ['bid-snapshot', profileId] });
      void qc.invalidateQueries({ queryKey: ['bid-snapshot-items', profileId] });
      void qc.invalidateQueries({ queryKey: ['bid-snapshot-apply-jobs', profileId] });
      void qc.invalidateQueries({ queryKey: ['bid-snapshot-apply-job', profileId] });
      void qc.invalidateQueries({ queryKey: ['bid-snapshot-apply-results', profileId] });
      return;
    }
    if (tab === AUTOMATIONS_TAB_SMART_BUDGET) {
      void qc.invalidateQueries({ queryKey: ['smart-budget-campaigns', profileId] });
      return;
    }
    invalidate();
  };

  const mut = useMutation({
    mutationFn: async (op: {
      type: string;
      id?: string;
      ids?: string[];
      run?: AutomationPendingRun;
    }) => {
      if (op.type === 'enable' && op.id) return automationApi.enableRule(profileId, op.id);
      if (op.type === 'disable' && op.id) return automationApi.disableRule(profileId, op.id);
      if (op.type === 'duplicate' && op.id) return automationApi.duplicateRule(profileId, op.id);
      if (op.type === 'delete' && op.id) return automationApi.deleteRule(profileId, op.id);
      if (op.type === 'execute' && op.id)
        return automationApi.executeRule(profileId, op.id, { dryRun: false });
      if (op.type === 'confirm' && op.id) return automationApi.confirmPending(profileId, op.id);
      if (op.type === 'cancel' && op.id) return automationApi.cancelPending(profileId, op.id);
      let ids = op.ids;
      if ((op.type === 'confirm-run' || op.type === 'cancel-run') && op.run) {
        const rows = await fetchAllPendingForRun({
          profileId,
          ruleId: op.run.ruleId,
          scheduledSlot: op.run.scheduledSlot,
        });
        ids = rows.filter((row) => row.status === 'pending').map((row) => row.id);
        if (!ids.length) throw new Error('No pending actions');
      }
      if ((op.type === 'confirm-batch' || op.type === 'confirm-run') && ids?.length)
        return automationApi.confirmPendingBatch(profileId, ids);
      if ((op.type === 'cancel-batch' || op.type === 'cancel-run') && ids?.length) {
        await Promise.all(ids.map((id) => automationApi.cancelPending(profileId, id)));
        return { success: true };
      }
      return null;
    },
    onSuccess: (_data, op) => {
      showToast('Done', 'success');
      invalidate();
      if (
        op.type === 'confirm-batch' ||
        op.type === 'cancel-batch' ||
        op.type === 'confirm-run' ||
        op.type === 'cancel-run'
      ) {
        setSelectedPendingRun(null);
      }
    },
    onError: (e: unknown) => {
      showToast(e instanceof Error ? e.message : 'Action failed', 'error');
    },
  });

  const filterRuleOptions = filterRulesQ.data?.items ?? [];

  return (
    <div className="mx-auto w-full max-w-[1380px] px-6 pb-8 pt-1">
      <header className="mb-4">
        <h1 className="text-2xl font-semibold text-foreground">Advanced Automation</h1>
        <p className="mt-0.5 text-sm text-foreground-muted">
          Rules that monitor performance and take actions automatically, plus budget, bid, and
          placement dayparting, smart budget, and bid snapshots for sale-time restore.
        </p>
      </header>

      <div className="mb-4 flex gap-4 border-b border-border text-sm">
        {[
          { id: 'rules', label: 'Rules' },
          { id: 'pending', label: 'Pending logs' },
          { id: 'executed', label: 'Executed' },
        ].map((t) => (
          <button
            key={t.id}
            type="button"
            className={cn(
              'border-b-2 px-1 pb-2',
              (t.id === AUTOMATIONS_TAB_RULES ? isRulesTabActive(tab) : tab === t.id)
                ? 'border-brand text-brand'
                : 'border-transparent text-foreground-muted'
            )}
            onClick={() => setTabParams({ tab: t.id })}
          >
            {t.label}
          </button>
        ))}
      </div>

      {(tab === 'rules' ||
        tab === 'bid-snapshots' ||
        tab === DAYPARTING_TAB ||
        tab === AUTOMATIONS_TAB_SMART_BUDGET) && (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          {AUTOMATION_RULE_CHIPS.map((e) => (
            <button
              key={e.id}
              type="button"
              className={cn(
                'rounded-full px-3 py-1 text-sm',
                tab === 'rules' && entityType === e.id
                  ? 'bg-brand text-white'
                  : 'bg-surface-muted text-foreground-muted'
              )}
              onClick={() =>
                setTabParams({ tab: AUTOMATIONS_TAB_RULES, entity: e.id, rule: '', section: '' })
              }
            >
              {e.label}
            </button>
          ))}
          <button
            type="button"
            className={cn(
              'rounded-full px-3 py-1 text-sm',
              tab === 'bid-snapshots' ? 'bg-brand text-white' : 'bg-surface-muted text-foreground-muted'
            )}
            onClick={() =>
              setTabParams({
                tab: AUTOMATIONS_TAB_BID_SNAPSHOTS,
                entity: '',
                rule: '',
                section: AUTOMATIONS_TAB_BID_SNAPSHOTS,
              })
            }
          >
            Bid snapshots
          </button>
          <button
            type="button"
            className={cn(
              'rounded-full px-3 py-1 text-sm',
              tab === DAYPARTING_TAB ? 'bg-brand text-white' : 'bg-surface-muted text-foreground-muted'
            )}
            onClick={() =>
              setTabParams({
                tab: DAYPARTING_TAB,
                entity: '',
                rule: '',
                section: '',
              })
            }
          >
            Dayparting
          </button>
          <button
            type="button"
            className={cn(
              'rounded-full px-3 py-1 text-sm',
              tab === AUTOMATIONS_TAB_SMART_BUDGET
                ? 'bg-brand text-white'
                : 'bg-surface-muted text-foreground-muted'
            )}
            onClick={() =>
              setTabParams({
                tab: AUTOMATIONS_TAB_SMART_BUDGET,
                entity: '',
                rule: '',
                section: '',
              })
            }
          >
            Smart budget
          </button>
          {tab === 'rules' && (
            <div className="ml-auto flex flex-wrap items-center gap-2">
              {entityType !== 'term_list' && (
                <input
                  className="rounded border border-border px-3 py-1.5 text-sm"
                  placeholder="Search rules"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              )}
              {canWrite && (
                <Button
                  onClick={() => {
                    setEditRule(null);
                    setBuilderOpen(true);
                  }}
                >
                  <Plus className="mr-1 h-4 w-4" />
                  {entityType === 'term_list' ? 'Create list' : 'Create rule'}
                </Button>
              )}
            </div>
          )}
        </div>
      )}

      {tab === 'rules' && entityType === 'term_list' && (
        <TermListsSection
          profileId={profileId}
          canWrite={canWrite}
          createOpen={builderOpen}
          onCreateOpenChange={(open) => {
            setBuilderOpen(open);
            if (!open) setEditRule(null);
          }}
        />
      )}

      {tab === 'rules' && entityType !== 'term_list' && (
        <>
          {rulesQ.isLoading ? (
            <Spinner />
          ) : (
            <TableWrap>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Status</TableHead>
                    <TableHead>Rule</TableHead>
                    <TableHead>Conditions</TableHead>
                    <TableHead>Frequency</TableHead>
                    <TableHead>Last / Next</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <tbody>
                  {(rulesQ.data?.items ?? []).map((rule) => {
                    const badgeStatus = ruleBadgeStatus(rule);
                    const enableBlocked = badgeStatus === 'expired' && rule.status !== 'active';
                    return (
                    <TableRow key={rule.id}>
                      <TableCell>
                        <span
                          className={cn(
                            'rounded-full border px-2 py-0.5 text-xs',
                            statusBadgeClass(badgeStatus)
                          )}
                        >
                          {badgeStatus}
                        </span>
                        <div className="mt-1 text-[11px] text-foreground-muted">
                          {rule.endDate ? `Ends ${formatRuleDate(rule.endDate)}` : 'No end date'}
                        </div>
                      </TableCell>
                      <TableCell className="font-medium">
                        <div>{rule.name}</div>
                        <div className="text-xs font-normal text-foreground-muted">
                          {ruleScopeHint(
                            rule.entityScope,
                            rule.entities?.length ?? 0,
                            scopeHintNoun(
                              rule.entityType,
                              rule.entityScope,
                              rule.entities,
                              rule.ruleOptions as Record<string, unknown> | undefined
                            ) ?? (rule.entityType === 'placement' ? 'campaigns' : entityNoun(rule.entityType))
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="max-w-xs truncate text-foreground-muted">
                        {rule.conditionsSummary || '—'}
                      </TableCell>
                      <TableCell>{formatFrequencyCell(rule)}</TableCell>
                      <TableCell className="text-xs text-foreground-muted">
                        <div>{rule.lastRunAt ? new Date(rule.lastRunAt).toLocaleString() : '—'}</div>
                        <div>{rule.nextRunAt ? new Date(rule.nextRunAt).toLocaleString() : '—'}</div>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {canWrite && (
                            <>
                              <button
                                type="button"
                                title="Edit"
                                className="rounded p-1 hover:bg-surface-muted"
                                onClick={() => {
                                  setEditRule(rule);
                                  setBuilderOpen(true);
                                }}
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                title={
                                  rule.status === 'active'
                                    ? 'Pause'
                                    : enableBlocked
                                      ? 'End date has passed. Extend it or choose no end date first.'
                                      : 'Enable'
                                }
                                disabled={enableBlocked}
                                className={cn(
                                  'rounded p-1 hover:bg-surface-muted',
                                  enableBlocked && 'cursor-not-allowed opacity-40 hover:bg-transparent'
                                )}
                                onClick={() => {
                                  if (enableBlocked) return;
                                  mut.mutate({
                                    type: rule.status === 'active' ? 'disable' : 'enable',
                                    id: rule.id,
                                  });
                                }}
                              >
                                {rule.status === 'active' ? (
                                  <Pause className="h-4 w-4" />
                                ) : (
                                  <Play className="h-4 w-4" />
                                )}
                              </button>
                              <button
                                type="button"
                                title="Duplicate"
                                className="rounded p-1 hover:bg-surface-muted"
                                onClick={() => mut.mutate({ type: 'duplicate', id: rule.id })}
                              >
                                <Copy className="h-4 w-4" />
                              </button>
                              <button
                                type="button"
                                title="Delete"
                                className="rounded p-1 hover:bg-surface-muted"
                                onClick={() => mut.mutate({ type: 'delete', id: rule.id })}
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </>
                          )}
                          {canExecute && (
                            <button
                              type="button"
                              title="Run now"
                              className="rounded p-1 hover:bg-surface-muted"
                              onClick={() => mut.mutate({ type: 'execute', id: rule.id })}
                            >
                              <Zap className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                    );
                  })}
                  {(rulesQ.data?.items ?? []).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="py-8 text-center text-foreground-muted">
                        No rules yet.{' '}
                        {canWrite && (
                          <button type="button" className="text-brand" onClick={() => setBuilderOpen(true)}>
                            Create one
                          </button>
                        )}
                      </TableCell>
                    </TableRow>
                  )}
                </tbody>
              </Table>
            </TableWrap>
          )}
        </>
      )}

      {(tab === 'pending' || tab === 'executed') && (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <label className="text-sm text-foreground-muted" htmlFor="automation-rule-filter">
            Rule
          </label>
          <select
            id="automation-rule-filter"
            className="rounded border border-border bg-surface px-3 py-1.5 text-sm"
            value={ruleFilter}
            onChange={(e) => setTabParams({ tab, rule: e.target.value })}
          >
            <option value="">All rules</option>
            {filterRuleOptions.map((rule) => (
              <option key={rule.id} value={rule.id}>
                {rule.name}
              </option>
            ))}
          </select>
          {tab === 'executed' && (
            <>
              <label className="ml-2 text-sm text-foreground-muted" htmlFor="automation-outcome-filter">
                Show
              </label>
              <select
                id="automation-outcome-filter"
                className="rounded border border-border bg-surface px-3 py-1.5 text-sm"
                value={execOutcome}
                onChange={(e) =>
                  setExecOutcome(e.target.value as 'all' | AutomationExecutionOutcome)
                }
              >
                <option value="all">All</option>
                <option value="applied">Applied</option>
                <option value="unmatched">Conditions not met</option>
              </select>
              <div className="ml-auto">
                <ExecutionCsvExportButton
                  disabled={execQ.isLoading || (execQ.data?.total ?? 0) === 0}
                  onExport={exportExecutions}
                />
              </div>
            </>
          )}
        </div>
      )}

      {tab === 'pending' &&
        (pendingRunsQ.isLoading ? (
          <Spinner />
        ) : (
          <PendingRunsTable
            runs={pendingRuns}
            canExecute={canExecute}
            busy={mut.isPending}
            onOpen={setSelectedPendingRun}
            onConfirm={(run) => mut.mutate({ type: 'confirm-run', run })}
            onCancel={(run) => mut.mutate({ type: 'cancel-run', run })}
          />
        ))}

      {selectedPendingRun && (
        <PendingRunDetailModal
          profileId={profileId}
          run={selectedPendingRun}
          canExecute={canExecute}
          busy={mut.isPending}
          onClose={() => setSelectedPendingRun(null)}
          onConfirmOne={(id) => mut.mutate({ type: 'confirm', id })}
          onCancelOne={(id) => mut.mutate({ type: 'cancel', id })}
          onConfirmAll={(ids) => mut.mutate({ type: 'confirm-batch', ids })}
          onCancelAll={(ids) => mut.mutate({ type: 'cancel-batch', ids })}
        />
      )}

      {tab === 'executed' &&
        (execQ.isLoading ? (
          <Spinner />
        ) : execQ.isError ? (
          <p className="py-8 text-center text-foreground-muted">Failed to load executions</p>
        ) : (
          <TableWrap>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Rule</TableHead>
                  <TableHead>Entities</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Before</TableHead>
                  <TableHead>After</TableHead>
                  <TableHead>Time</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <tbody>
                {(execQ.data?.items ?? []).map((run) => {
                  const entityLabel = entityNoun(run.entityType);
                  const previewNames = run.previewNames.slice(0, 2).join(', ');
                  const extra = run.total > 2 ? ` +${run.total - 2}` : '';
                  return (
                    <TableRow
                      key={`${run.ruleId}:${run.scheduledSlot ?? run.executionTime ?? ''}`}
                      className="cursor-pointer hover:bg-surface-muted/60"
                      onClick={() => setSelectedExecRun(run)}
                    >
                      <TableCell className="font-medium">
                        {ruleLabel(run.ruleName, run.ruleId)}
                        {run.dryRun && (
                          <span className="ml-2 text-xs font-normal text-foreground-muted">
                            dry run
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">
                          {run.total} {entityLabel}
                        </div>
                        <div className="max-w-md truncate text-xs text-foreground-muted">
                          {previewNames}
                          {extra}
                        </div>
                      </TableCell>
                      <TableCell className="max-w-[180px] truncate text-xs">
                        {formatActionType(run.actionType)}
                      </TableCell>
                      <TableCell className="max-w-[160px] truncate text-xs">—</TableCell>
                      <TableCell className="max-w-[160px] truncate text-xs">—</TableCell>
                      <TableCell className="text-xs">
                        {run.executionTime
                          ? new Date(run.executionTime).toLocaleString()
                          : '—'}
                      </TableCell>
                      <TableCell>
                        <span
                          className={cn(
                            'rounded-full border px-2 py-0.5 text-xs',
                            statusBadgeClass(groupBadgeStatusFromCounts(run.statusCounts))
                          )}
                        >
                          {summarizeStatusCounts(run.statusCounts)}
                        </span>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {(execQ.data?.items ?? []).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="py-8 text-center text-foreground-muted">
                      No executions yet
                    </TableCell>
                  </TableRow>
                )}
              </tbody>
            </Table>
          </TableWrap>
        ))}

      {tab === 'bid-snapshots' && profileId ? (
        <BidSnapshotsSection profileId={profileId} />
      ) : null}

      {tab === DAYPARTING_TAB && profileId ? (
        <div className="space-y-3">
          <div className="inline-flex rounded-lg border border-border p-0.5" role="tablist">
            {(
              [
                { id: 'budget', label: 'Budget' },
                { id: 'bid', label: 'Bid' },
                { id: 'placement', label: 'Placement' },
              ] as const
            ).map((item) => (
              <button
                key={item.id}
                type="button"
                role="tab"
                aria-selected={daypartingKind === item.id}
                className={cn(
                  'rounded-md px-3 py-1.5 text-sm font-medium',
                  daypartingKind === item.id
                    ? 'bg-brand/10 text-foreground'
                    : 'text-foreground-muted hover:text-foreground'
                )}
                onClick={() => setDaypartingKind(item.id)}
              >
                {item.label}
              </button>
            ))}
          </div>
          {daypartingKind === 'budget' ? (
            <DaypartingSection profileId={profileId} />
          ) : (
            <BidPlacementSection profileId={profileId} kind={daypartingKind} />
          )}
        </div>
      ) : null}

      {tab === AUTOMATIONS_TAB_SMART_BUDGET && profileId ? (
        <SmartBudgetSection
          profileId={profileId}
          onApplied={() =>
            setTabParams({
              tab: DAYPARTING_TAB,
              entity: '',
              rule: '',
              section: '',
            })
          }
        />
      ) : null}

      {selectedExecRun && (
        <ExecutionRunDetailModal
          profileId={profileId}
          run={selectedExecRun}
          onClose={() => setSelectedExecRun(null)}
        />
      )}

      {builderOpen && entityType !== 'term_list' && (
        <RuleBuilderModal
          profileId={profileId}
          entityType={entityType}
          initial={editRule}
          onClose={() => {
            setBuilderOpen(false);
            setEditRule(null);
          }}
        />
      )}

      <div className="mt-6 text-xs text-foreground-muted">
        <button type="button" className="text-brand" onClick={refreshCurrentTab}>
          Refresh
        </button>
      </div>
    </div>
  );
}
